import {ChevronDownIcon, ChevronRightIcon, TableIcon} from "lucide-react";
import React, {useCallback, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Card, CardContent} from "@components/ui/card";
import {Input} from "@components/ui/input";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {commitOps, PeriodOptions, useVNextBS, VNextChecksSummary, VNextComparisonBlock, VNextMonth, VNextRow} from "@hooks/use-vnext";

import {ChecksPanel} from "./ChecksPanel";
import {ProvenanceErrorBoundary} from "./ProvenanceErrorBoundary";
import {ProvenancePanel} from "./ProvenancePanel";

interface BalanceSheetTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    compare?: string;
    fyStartMonth?: number;
    currentMonth?: string;
}

interface SelectedCell {
    nodeId: string;
    nodeLabel: string;
    monthKey: string;
    monthLabel: string;
    value: number;
    isForecast: boolean;
    accountCode: string;
}

function formatCurrency(value: number): string {
    return Math.abs(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
}

function formatVariance(value: number | undefined): string {
    if (value === undefined || value === null || value === 0) return "\u2014";
    const formatted = Math.abs(value).toLocaleString("en-AU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    return value < 0 ? `(${formatted})` : formatted;
}

// Synthetic node IDs that should not be editable
const SYNTHETIC_IDS = new Set([
    "__bs_total_assets__",
    "__bs_total_liab_equity__",
    "__bs_balance_check__",
]);

function isSyntheticRow(nodeId: string): boolean {
    return SYNTHETIC_IDS.has(nodeId);
}

function isLinkedRow(row: VNextRow): boolean {
    return !!row.linked_from;
}

export function BalanceSheetTab({workspaceId, scenarioId, actualMonths, forecastMonths = 6, period, selectedMonth, customFrom, customTo, compare, fyStartMonth, currentMonth}: BalanceSheetTabProps) {
    const periodOpts: PeriodOptions = {period, selectedMonth, customFrom, customTo, compare, fyStartMonth, currentMonth};
    const {data, months, comparison, isLoading, error, refresh} = useVNextBS(workspaceId, scenarioId, forecastMonths, actualMonths, periodOpts);
    const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
    const [editingCell, setEditingCell] = useState<{nodeId: string; monthKey: string} | null>(null);
    const [editValue, setEditValue] = useState("");
    const [checkFiguresOpen, setCheckFiguresOpen] = useState(false);

    const handleCellClick = useCallback((cell: SelectedCell) => {
        setSelectedCell(cell);
    }, []);

    const handleCellDoubleClick = useCallback((nodeId: string, monthKey: string, value: number, row: VNextRow) => {
        // Don't allow editing on linked or synthetic cells
        if (isSyntheticRow(nodeId) || isLinkedRow(row)) return;
        setEditingCell({nodeId, monthKey});
        setEditValue(String(Math.round(value)));
    }, []);

    const handleOverrideCommit = useCallback(
        async (nodeId: string, monthKey: string) => {
            const newValue = Number(editValue);
            if (isNaN(newValue)) {
                setEditingCell(null);
                return;
            }
            try {
                await commitOps(
                    workspaceId,
                    [{op_type: "set_override", target_node_id: nodeId, target_month: monthKey, params: {value: newValue}}],
                    scenarioId,
                );
                refresh();
            } catch {
                // Override failed silently
            }
            setEditingCell(null);
        },
        [editValue, workspaceId, scenarioId, refresh],
    );

    const handleEditCancel = useCallback(() => {
        setEditingCell(null);
    }, []);

    if (isLoading) {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="space-y-3">
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-6 w-full" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-red-600">{error}</CardContent>
            </Card>
        );
    }

    if (months.length === 0) {
        return (
            <Card>
                <CardContent className="p-12 text-center text-muted-foreground">
                    <TableIcon className="mx-auto mb-3 size-8" />
                    <p className="text-lg font-medium">No Balance Sheet data yet</p>
                    <p className="mt-1 text-sm">
                        Seed your workspace from Xero data to populate the Balance Sheet.
                    </p>
                </CardContent>
            </Card>
        );
    }

    // Collect unique node IDs in order, excluding synthetics for grouping
    const nodeIds: string[] = [];
    const seen = new Set<string>();
    for (const m of months) {
        for (const row of m.rows) {
            if (!seen.has(row.node_id) && !isSyntheticRow(row.node_id)) {
                seen.add(row.node_id);
                nodeIds.push(row.node_id);
            }
        }
    }

    // Build row data
    const rowData = nodeIds.map((nodeId) => {
        const values = new Map<string, VNextRow>();
        for (const m of months) {
            const row = m.rows.find((r) => r.node_id === nodeId);
            if (row) values.set(m.month_key, row);
        }
        const firstRow = values.values().next().value;
        return {
            nodeId,
            label: firstRow?.label ?? nodeId,
            category: firstRow?.category_key ?? "",
            values,
        };
    });

    // Filter out all-zero rows
    const filteredRowData = rowData.filter((row) => {
        for (const r of row.values.values()) {
            if (Math.abs(r.value) >= 0.01) return true;
        }
        return false;
    });

    // Group by BS sections
    const categoryGroups = new Map<string, typeof rowData>();
    for (const row of filteredRowData) {
        const topCat = row.category.split(".")[0] || "other";
        if (!categoryGroups.has(topCat)) categoryGroups.set(topCat, []);
        categoryGroups.get(topCat)!.push(row);
    }

    const SECTION_ORDER = ["assets", "liabilities", "equity"];
    const orderedSections = SECTION_ORDER
        .filter((cat) => categoryGroups.has(cat))
        .map((cat) => [cat, categoryGroups.get(cat)!] as const);

    const SECTION_LABELS: Record<string, string> = {
        assets: "Assets",
        liabilities: "Liabilities",
        equity: "Equity",
    };

    // Compute section totals
    const sectionTotals = new Map<string, Map<string, number>>();
    for (const [cat, rows] of orderedSections) {
        const totals = new Map<string, number>();
        for (const m of months) {
            let total = 0;
            for (const row of rows) {
                const v = row.values.get(m.month_key);
                if (v) total += v.value;
            }
            totals.set(m.month_key, total);
        }
        sectionTotals.set(cat, totals);
    }

    // Get synthetic totals from engine
    const getSyntheticValue = (month: VNextMonth, nodeId: string): number => {
        const row = month.rows.find((r) => r.node_id === nodeId);
        return row?.value ?? 0;
    };

    return (
        <div className="flex flex-col flex-1 min-h-0 min-w-0 gap-3">
            <div className="shrink-0">
                <ChecksPanel checksSummary={(data as unknown as {checks_summary?: VNextChecksSummary})?.checks_summary} checksScope={(data as unknown as {checks_scope?: "full_model"})?.checks_scope} />
            </div>

            <div className="shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <strong>Prior version</strong> — This balance sheet view is retained for reference only. The Modeling Workbench and Forecast Summary now provide the current 3-statement model.
            </div>

            <div className="shrink-0 flex items-center gap-2">
                <TableIcon className="size-5 text-[#209DB3]" />
                <h2 className="text-lg font-semibold">Balance Sheet</h2>
                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 ml-1">BETA</Badge>
                <span className="text-xs text-muted-foreground ml-auto">
                    Click cell for details. <span className="text-[#209DB3] font-medium">Coloured values</span> are linked from P&L/CF.
                </span>
            </div>

            <TooltipProvider>
                <Card className="grid-card flex-1 min-h-0 !py-0 !gap-0">
                    <Table className="w-auto" style={{minWidth: `max(100%, ${180 + months.length * 100 + (comparison ? comparison.months.length * 100 + 2 : 0)}px)`}}>
                        <TableHeader className="sticky top-0 z-20">
                            <TableRow className="bg-background">
                                <TableHead className="sticky left-0 z-30 bg-background min-w-[180px]">
                                    Account
                                </TableHead>
                                {months.map((m) => (
                                    <TableHead key={m.month_key} className="text-right min-w-[100px] bg-background">
                                        <div className="flex flex-col items-end">
                                            <span>{m.label}</span>
                                            <Badge
                                                variant={m.status === "actual" ? "default" : "outline"}
                                                className="mt-0.5 text-[10px] px-1 py-0"
                                            >
                                                {m.status}
                                            </Badge>
                                        </div>
                                    </TableHead>
                                ))}
                                {comparison && (
                                    <>
                                        <TableHead className="w-[2px] px-0 bg-background">
                                            <div className="w-[2px] h-full bg-amber-300" />
                                        </TableHead>
                                        {comparison.months.map((m) => (
                                            <TableHead key={`cmp-${m.month_key}`} className="text-right min-w-[100px] bg-amber-50/50 dark:bg-amber-950/20">
                                                <div className="flex flex-col items-end">
                                                    <span className="text-amber-700 dark:text-amber-400">{m.label}</span>
                                                    <Badge variant="outline" className="mt-0.5 text-[10px] px-1 py-0 border-amber-400 text-amber-600">
                                                        {comparison.mode === "yoy" ? "YoY" : "Prev"}
                                                    </Badge>
                                                </div>
                                            </TableHead>
                                        ))}
                                    </>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {orderedSections.map(([category, rows]) => (
                                <BSSection
                                    key={category}
                                    categoryLabel={SECTION_LABELS[category] || category}
                                    rows={rows}
                                    months={months}
                                    comparison={comparison}
                                    editingCell={editingCell}
                                    editValue={editValue}
                                    onCellClick={handleCellClick}
                                    onCellDoubleClick={handleCellDoubleClick}
                                    onEditChange={setEditValue}
                                    onEditCommit={handleOverrideCommit}
                                    onEditCancel={handleEditCancel}
                                />
                            ))}

                            {/* Total Assets */}
                            <TableRow className="border-t-2 bg-emerald-50/50 dark:bg-emerald-950/20 font-bold">
                                <TableCell className="sticky left-0 z-20 bg-emerald-50 dark:bg-emerald-950 text-sm font-bold">
                                    Total Assets
                                </TableCell>
                                {months.map((m) => {
                                    const val = getSyntheticValue(m, "__bs_total_assets__");
                                    return (
                                        <TableCell key={m.month_key} className="text-right tabular-nums text-sm font-bold">
                                            ${formatCurrency(val)}
                                        </TableCell>
                                    );
                                })}
                                {comparison && (
                                    <>
                                        <TableCell className="w-[2px] px-0" />
                                        {comparison.months.map((cm) => {
                                            const val = getSyntheticValue(cm, "__bs_total_assets__");
                                            return (
                                                <TableCell key={`cmp-ta-${cm.month_key}`} className="text-right tabular-nums text-sm font-bold text-amber-700/70 dark:text-amber-400/70 bg-amber-50/30 dark:bg-amber-950/10">
                                                    ${formatCurrency(val)}
                                                </TableCell>
                                            );
                                        })}
                                    </>
                                )}
                            </TableRow>

                            {/* Total L+E */}
                            <TableRow className="bg-blue-50/50 dark:bg-blue-950/20 font-bold">
                                <TableCell className="sticky left-0 z-20 bg-blue-50 dark:bg-blue-950 text-sm font-bold">
                                    Total Liabilities + Equity
                                </TableCell>
                                {months.map((m) => {
                                    const val = getSyntheticValue(m, "__bs_total_liab_equity__");
                                    return (
                                        <TableCell key={m.month_key} className="text-right tabular-nums text-sm font-bold">
                                            ${formatCurrency(val)}
                                        </TableCell>
                                    );
                                })}
                                {comparison && (
                                    <>
                                        <TableCell className="w-[2px] px-0" />
                                        {comparison.months.map((cm) => {
                                            const val = getSyntheticValue(cm, "__bs_total_liab_equity__");
                                            return (
                                                <TableCell key={`cmp-tle-${cm.month_key}`} className="text-right tabular-nums text-sm font-bold text-amber-700/70 dark:text-amber-400/70 bg-amber-50/30 dark:bg-amber-950/10">
                                                    ${formatCurrency(val)}
                                                </TableCell>
                                            );
                                        })}
                                    </>
                                )}
                            </TableRow>

                            {/* Check Figures — collapsible */}
                            <TableRow className="border-t-2 bg-muted/30 font-bold cursor-pointer select-none" onClick={() => setCheckFiguresOpen(!checkFiguresOpen)}>
                                <TableCell className="sticky left-0 z-20 bg-muted text-sm font-bold" colSpan={months.length + 1 + (comparison ? comparison.months.length + 1 : 0)}>
                                    <span className="inline-flex items-center gap-1">
                                        {checkFiguresOpen ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                                        Check Figures
                                    </span>
                                </TableCell>
                            </TableRow>

                            {checkFiguresOpen && (
                            <>
                            {/* Balance Check */}
                            <TableRow className="bg-muted/30 font-bold">
                                <TableCell className="sticky left-0 z-20 bg-muted text-sm font-bold">
                                    Balance Check
                                </TableCell>
                                {months.map((m) => {
                                    const val = getSyntheticValue(m, "__bs_balance_check__");
                                    const isBalanced = Math.abs(val) < 1;
                                    return (
                                        <TableCell
                                            key={m.month_key}
                                            className={`text-right tabular-nums text-sm font-bold ${
                                                isBalanced ? "text-green-600" : "text-red-600"
                                            }`}
                                        >
                                            ${formatCurrency(val)}
                                        </TableCell>
                                    );
                                })}
                                {comparison && (
                                    <>
                                        <TableCell className="w-[2px] px-0" />
                                        {comparison.months.map((cm) => {
                                            const val = getSyntheticValue(cm, "__bs_balance_check__");
                                            const isBalanced = Math.abs(val) < 1;
                                            return (
                                                <TableCell key={`cmp-bc-${cm.month_key}`} className={`text-right tabular-nums text-sm font-bold bg-amber-50/30 dark:bg-amber-950/10 ${isBalanced ? "text-green-600/70" : "text-red-600/70"}`}>
                                                    ${formatCurrency(val)}
                                                </TableCell>
                                            );
                                        })}
                                    </>
                                )}
                            </TableRow>

                            {/* Xero Balance Sheet Check */}
                            {(data?.xero_check_figures?.length ?? 0) > 0 && (
                                <>
                                    <TableRow className="bg-muted/40 border-t-2">
                                        <TableCell
                                            colSpan={months.length + 1 + (comparison ? comparison.months.length + 1 : 0)}
                                            className="sticky left-0 py-1"
                                        >
                                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                                Xero Balance Sheet Check
                                                {data!.xero_check_figures!.every((f) => f.total === 0)
                                                    ? " \u2014 Tied"
                                                    : ""}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                    {data!.xero_check_figures!.map((fig) => {
                                        const hasDebug = fig.model_months && fig.xero_months;
                                        return (
                                            <React.Fragment key={`xero-bs-${fig.key}`}>
                                                {hasDebug && (
                                                    <TableRow className="bg-muted/20">
                                                        <TableCell className="sticky left-0 bg-muted z-20 text-xs whitespace-nowrap py-0.5 pl-6 text-muted-foreground">
                                                            {fig.label} (Model)
                                                        </TableCell>
                                                        {months.map((m) => {
                                                            const value = fig.model_months?.[m.month_key];
                                                            return (
                                                                <TableCell key={m.month_key} className="text-right tabular-nums whitespace-nowrap text-xs py-0.5 text-muted-foreground">
                                                                    {value !== undefined ? formatVariance(value) : "\u2014"}
                                                                </TableCell>
                                                            );
                                                        })}
                                                        {comparison && (
                                                            <>
                                                                <TableCell className="w-[2px] px-0" />
                                                                {comparison.months.map((cm) => (
                                                                    <TableCell key={`cmp-m-${cm.month_key}`} className="text-right tabular-nums whitespace-nowrap text-xs py-0.5 text-muted-foreground bg-amber-50/30 dark:bg-amber-950/10">
                                                                        {fig.model_months?.[cm.month_key] !== undefined ? formatVariance(fig.model_months[cm.month_key]) : "\u2014"}
                                                                    </TableCell>
                                                                ))}
                                                            </>
                                                        )}
                                                    </TableRow>
                                                )}
                                                {hasDebug && (
                                                    <TableRow className="bg-muted/20">
                                                        <TableCell className="sticky left-0 bg-muted z-20 text-xs whitespace-nowrap py-0.5 pl-6 text-muted-foreground">
                                                            {fig.label} (Xero)
                                                        </TableCell>
                                                        {months.map((m) => {
                                                            const value = fig.xero_months?.[m.month_key];
                                                            return (
                                                                <TableCell key={m.month_key} className="text-right tabular-nums whitespace-nowrap text-xs py-0.5 text-muted-foreground">
                                                                    {value !== undefined ? formatVariance(value) : "\u2014"}
                                                                </TableCell>
                                                            );
                                                        })}
                                                        {comparison && (
                                                            <>
                                                                <TableCell className="w-[2px] px-0" />
                                                                {comparison.months.map((cm) => (
                                                                    <TableCell key={`cmp-x-${cm.month_key}`} className="text-right tabular-nums whitespace-nowrap text-xs py-0.5 text-muted-foreground bg-amber-50/30 dark:bg-amber-950/10">
                                                                        {fig.xero_months?.[cm.month_key] !== undefined ? formatVariance(fig.xero_months[cm.month_key]) : "\u2014"}
                                                                    </TableCell>
                                                                ))}
                                                            </>
                                                        )}
                                                    </TableRow>
                                                )}
                                                <TableRow className="bg-muted/40">
                                                    <TableCell className="sticky left-0 bg-muted z-20 text-xs whitespace-nowrap py-1">
                                                        {fig.label}{hasDebug ? " (Var)" : ""}
                                                    </TableCell>
                                                    {months.map((m) => {
                                                        const value = fig.months?.[m.month_key];
                                                        const isMissing = value === undefined || value === null;
                                                        return (
                                                            <TableCell
                                                                key={m.month_key}
                                                                className={`text-right tabular-nums whitespace-nowrap text-xs py-1 ${
                                                                    isMissing
                                                                        ? "text-muted-foreground/40"
                                                                        : value !== 0
                                                                            ? "text-destructive font-medium"
                                                                            : "text-emerald-600"
                                                                }`}
                                                            >
                                                                {isMissing ? "\u2014" : formatVariance(value)}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    {comparison && (
                                                        <>
                                                            <TableCell className="w-[2px] px-0" />
                                                            {comparison.months.map((cm) => {
                                                                const value = fig.months?.[cm.month_key];
                                                                const isMissing = value === undefined || value === null;
                                                                return (
                                                                    <TableCell
                                                                        key={`cmp-xbs-${cm.month_key}`}
                                                                        className={`text-right tabular-nums whitespace-nowrap text-xs py-1 bg-amber-50/30 dark:bg-amber-950/10 ${
                                                                            isMissing
                                                                                ? "text-muted-foreground/40"
                                                                                : value !== 0
                                                                                    ? "text-destructive/70 font-medium"
                                                                                    : "text-emerald-600/70"
                                                                        }`}
                                                                    >
                                                                        {isMissing ? "\u2014" : formatVariance(value)}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </>
                                                    )}
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                            )}
                            </>
                            )}
                        </TableBody>
                    </Table>
                </Card>
            </TooltipProvider>

            {selectedCell && (
                <ProvenanceErrorBoundary onClose={() => setSelectedCell(null)}>
                    <ProvenancePanel
                        workspaceId={workspaceId}
                        scenarioId={scenarioId}
                        nodeId={selectedCell.nodeId}
                        nodeLabel={selectedCell.nodeLabel}
                        monthKey={selectedCell.monthKey}
                        monthLabel={selectedCell.monthLabel}
                        value={selectedCell.value}
                        isForecast={selectedCell.isForecast}
                        onClose={() => setSelectedCell(null)}
                        onOpenTransactions={(filters) => {
                            window.dispatchEvent(new CustomEvent("vnext-open-transactions", {detail: {filters}}));
                        }}
                        source="pnl"
                        accountCode={selectedCell.accountCode}
                    />
                </ProvenanceErrorBoundary>
            )}
        </div>
    );
}

function BSSection({
    categoryLabel,
    rows,
    months,
    comparison,
    editingCell,
    editValue,
    onCellClick,
    onCellDoubleClick,
    onEditChange,
    onEditCommit,
    onEditCancel,
}: {
    categoryLabel: string;
    rows: {nodeId: string; label: string; category: string; values: Map<string, VNextRow>}[];
    months: VNextMonth[];
    comparison: VNextComparisonBlock | null;
    editingCell: {nodeId: string; monthKey: string} | null;
    editValue: string;
    onCellClick: (cell: SelectedCell) => void;
    onCellDoubleClick: (nodeId: string, monthKey: string, value: number, row: VNextRow) => void;
    onEditChange: (value: string) => void;
    onEditCommit: (nodeId: string, monthKey: string) => void;
    onEditCancel: () => void;
}) {
    const totals = new Map<string, number>();
    for (const m of months) {
        let total = 0;
        for (const row of rows) {
            const v = row.values.get(m.month_key);
            if (v) total += v.value;
        }
        totals.set(m.month_key, total);
    }

    const totalColSpan = months.length + 1 + (comparison ? comparison.months.length + 1 : 0);

    return (
        <>
            {/* Category header */}
            <TableRow className="bg-muted/50">
                <TableCell
                    colSpan={totalColSpan}
                    className="sticky left-0 z-20 bg-muted font-semibold text-xs uppercase tracking-wider"
                >
                    {categoryLabel}
                </TableCell>
            </TableRow>

            {/* Individual rows */}
            {rows.map((row) => (
                <TableRow key={row.nodeId} className="hover:bg-muted/30">
                    <TableCell className="sticky left-0 z-10 bg-background pl-6 text-sm">
                        {row.label}
                    </TableCell>
                    {months.map((m) => {
                        const v = row.values.get(m.month_key);
                        const value = v?.value ?? 0;
                        const isForecast = m.status === "forecast";
                        const linked = v && isLinkedRow(v);
                        const isEditing =
                            editingCell?.nodeId === row.nodeId && editingCell?.monthKey === m.month_key;

                        if (isEditing) {
                            return (
                                <TableCell key={m.month_key} className="text-right p-1">
                                    <Input
                                        autoFocus
                                        type="number"
                                        value={editValue}
                                        onChange={(e) => onEditChange(e.target.value)}
                                        onBlur={() => onEditCommit(row.nodeId, m.month_key)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") onEditCommit(row.nodeId, m.month_key);
                                            else if (e.key === "Escape") onEditCancel();
                                        }}
                                        className="h-7 w-24 text-right text-xs ml-auto"
                                    />
                                </TableCell>
                            );
                        }

                        return (
                            <Tooltip key={m.month_key}>
                                <TooltipTrigger asChild>
                                    <TableCell
                                        className={`text-right tabular-nums text-sm cursor-pointer transition-colors ${
                                            linked && isForecast
                                                ? "text-[#209DB3] font-semibold hover:bg-teal-50 dark:hover:bg-teal-950/30"
                                                : isForecast
                                                  ? "text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                                                  : "hover:bg-muted/50"
                                        } ${value < 0 ? "text-red-600" : ""}`}
                                        onClick={() =>
                                            onCellClick({
                                                nodeId: row.nodeId,
                                                nodeLabel: row.label,
                                                monthKey: m.month_key,
                                                monthLabel: m.label,
                                                value,
                                                isForecast,
                                                accountCode: v?.xero_account_id ?? "",
                                            })
                                        }
                                        onDoubleClick={() => {
                                            if (isForecast && v) onCellDoubleClick(row.nodeId, m.month_key, value, v);
                                        }}
                                    >
                                        ${formatCurrency(value)}
                                    </TableCell>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    <div className="text-xs space-y-1 max-w-[240px]">
                                        <p className="font-medium">{row.label} &middot; {m.label}</p>
                                        <div className="flex justify-between gap-4">
                                            <span className="text-muted-foreground">{isForecast ? "Forecast" : "Actual"}</span>
                                            <span className="font-mono font-medium">${formatCurrency(value)}</span>
                                        </div>
                                        {linked && isForecast && (
                                            <p className="text-[#209DB3] text-[10px]">
                                                Linked from {v?.linked_from === "pnl" ? "P&L (retained earnings)" : "Cash Flow (closing cash)"}
                                            </p>
                                        )}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                    {comparison && (
                        <>
                            <TableCell className="w-[2px] px-0" />
                            {comparison.months.map((cm) => {
                                const cmpRow = cm.rows.find((r) => r.node_id === row.nodeId);
                                return (
                                    <TableCell key={`cmp-${cm.month_key}`} className="text-right tabular-nums text-sm text-amber-700/70 dark:text-amber-400/70 bg-amber-50/30 dark:bg-amber-950/10">
                                        ${formatCurrency(cmpRow?.value ?? 0)}
                                    </TableCell>
                                );
                            })}
                        </>
                    )}
                </TableRow>
            ))}

            {/* Category total */}
            <TableRow className="border-t font-semibold">
                <TableCell className="sticky left-0 z-10 bg-background pl-4 text-sm">
                    Total {categoryLabel}
                </TableCell>
                {months.map((m) => {
                    const total = totals.get(m.month_key) ?? 0;
                    return (
                        <TableCell
                            key={m.month_key}
                            className={`text-right tabular-nums text-sm font-semibold ${
                                total < 0 ? "text-red-600" : ""
                            }`}
                        >
                            ${formatCurrency(total)}
                        </TableCell>
                    );
                })}
                {comparison && (
                    <>
                        <TableCell className="w-[2px] px-0" />
                        {comparison.months.map((cm) => {
                            const cmpTotal = rows.reduce((sum, row) => {
                                const cmpRow = cm.rows.find((r) => r.node_id === row.nodeId);
                                return sum + (cmpRow?.value ?? 0);
                            }, 0);
                            return (
                                <TableCell key={`cmp-total-${cm.month_key}`} className="text-right tabular-nums text-sm font-semibold text-amber-700/70 dark:text-amber-400/70 bg-amber-50/30 dark:bg-amber-950/10">
                                    ${formatCurrency(cmpTotal)}
                                </TableCell>
                            );
                        })}
                    </>
                )}
            </TableRow>
        </>
    );
}
